import React, { useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import axios from "axios";
import { useCompanyContext } from "../hooks/useCompanyContext";
import { useCultivationCompanyContext } from "../hooks/useCultivationCompanyContext";

const Teelt = () => {
  const { company } = useCompanyContext();
  const { t } = useCompanyContext();
  const { cultivationCompany } = useCultivationCompanyContext();

  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState([
    { id: "IA_PromisedDeliveryDate", desc: true },
  ]);
  const [loadingOrder, setLoadingOrder] = useState(null);

  const filteredData = useMemo(() => {
    const allowedStatuses = [
      "GOEDGEKEURDKLANT",
      "RESERVERING",
      "GOEDGEKEURD WPK",
      "5 - AFGEHANDELD",
    ];
    const orders = company?.orders || [];

    const statusFiltered = orders.filter((order) =>
      allowedStatuses.includes(order.IA_StatusCode)
    );

    if (!search) return statusFiltered;

    const searchTerm = search.trim().toLowerCase();

    return statusFiltered.filter((order) => {
      // Bestaande zoekfunctie over alle ordervelden
      const matchesOtherFields = Object.values(order).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm)
      );

      // Leverdatum omzetten naar Nederlandse notatie
      const deliveryDate = order.IA_PromisedDeliveryDate;

      const formattedDate = deliveryDate
        ? new Date(deliveryDate).toLocaleDateString("nl-NL", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : "";

      // Zoeken op alle velden OF de Nederlandse leverdatum
      return (
        matchesOtherFields ||
        formattedDate.includes(searchTerm)
      );
    });
  }, [company?.orders, search]);

  const handleDownload = async (orderNo, statusCode) => {
    if (!cultivationCompany || !cultivationCompany.name) {
      alert("Geen geldig bedrijf geselecteerd.");
      return;
    }

    setLoadingOrder(orderNo);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/orders/pdf/${orderNo}`,
        {
          responseType: "arraybuffer",
          headers: {
            "X-Cultivation-Company": cultivationCompany.name,
          },
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const newTab = window.open(url, "_blank");
      if (!newTab) {
        alert(
          "Pop-ups zijn geblokkeerd door de browser. Sta pop-ups toe om de PDF te openen."
        );
        return;
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 5000);
    } catch (error) {
      console.error(`Fout bij het ophalen van order ${orderNo}:`, error);
      
      if (statusCode === "RESERVERING") {
        alert(
          t.orderNietCompleetForecast
        );
      } else {
        alert("Er is een fout opgetreden bij het downloaden van de order-PDF.");
      }
    } finally {
      setLoadingOrder(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "No",
        header: t.ordernummer,
        meta: { className: "col-ordernummer" },
      },
      {
        id: "download",
        header: "",
        meta: { className: "col-detail" },
        cell: ({ row }) => (
          <button
            onClick={() => handleDownload(row.original.No, row.original.IA_StatusCode)}
            disabled={loadingOrder === row.original.No}
            className="download-button"
          >
            {loadingOrder === row.original.No ? "Bezig..." : "Detail"}
          </button>
        ),
      },
      {
        accessorKey: "IA_StatusCode",
        header: t.orderstatus,
        meta: { className: "col-status" },
      },
      {
        accessorKey: "Sell_to_Customer_Name",
        header: t.naam,
        meta: { className: "col-naam" },
      },
      {
        accessorKey: "Ship_to_Name",
        header: t.verzendnaam,
        meta: { className: "col-verzendnaam" },
      },
      {
        accessorKey: "IA_PromisedDeliveryDate",
        header: t.leverdatum,
        meta: { className: "col-leverdatum" },
        cell: (info) => {
          const dateValue = info.getValue();
          return dateValue
            ? new Date(dateValue).toLocaleDateString("nl-NL", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "N.v.t.";
        },
      },
    ],
    [loadingOrder, t, cultivationCompany]
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } },
  });

  if (!company || !company.orders || company.orders.length === 0) {
    return (
      <div>
        <h1 className="pageheading">{t.orderlijst}</h1>
        <p>Geen orders beschikbaar.</p>
      </div>
    );
  }

  return (
    <div className="order-table">
      <h1 className="pageheading">{t.orderlijst}</h1>

      <div className="search-container">
        <input
          type="text"
          placeholder={t.zoekPlaceholderOrderinformatie}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      <table>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={header.column.columnDef.meta?.className}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={cell.column.columnDef.meta?.className}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination">
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          Vorige
        </button>
        <span>
          Pagina{" "}
          <strong>
            {table.getState().pagination.pageIndex + 1} van{" "}
            {table.getPageCount()}
          </strong>
        </span>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          Volgende
        </button>
      </div>

      <style>
        {`
          /* Tablet en kleiner: minder belangrijke kolommen weg */
          @media (max-width: 1100px) {
            .col-verzendnaam {
              display: none;
            }
          }

          /* Mobiel landschap */
          @media (max-width: 900px) {
            .col-naam {
              display: none;
            }
          }

          /* Mobiel portret: nog compacter */
          @media (max-width: 700px) {

            table th,
            table td {
              font-size: 13px;
              padding: 6px 8px;
            }

            .download-button {
              padding: 6px 8px;
              font-size: 13px;
            }
          }

          /* Altijd: scroll bij overflow */
          .order-table {
            overflow-x: auto;
          }
        `}
      </style>
    </div>
  );
};

export default Teelt;
